import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { isChrome } from "react-device-detect";
import mixpanel from "mixpanel-browser";
import logo from "../../../sidebar-logo.png";
import userStore from "../../../Stores/User";
import { isPaidProUser, hasPaidPlan } from "../../../constants/plans";
import { createReactivationCheckoutSession } from "../../../audiblogsApi";
import { ReactComponent as Home } from "../Icons/Home.svg";
import { ReactComponent as Queue } from "../Icons/Queue.svg";
import { ReactComponent as Plus } from "../Icons/Plus.svg";
import Profile from "../Icons/Profile.svg";

import { Link } from "react-router-dom";
// import RssFeedIcon from "@material-ui/icons/RssFeed";
// import ExploreIcon from "@material-ui/icons/Explore";
// import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
// import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import {
  RssFeed as RssFeedIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  AccountCircle as AccountCircleIcon,
} from "@mui/icons-material";

import { version } from "../../../../package.json";

const ProBadge = observer(() => {
  const isProUser = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id 
    ? isPaidProUser(userStore.user)
    : false;
  const hasPlan = hasPaidPlan(userStore.user);

  const [showPopover, setShowPopover] = useState(false);

  const badgeStyle = {
    background: isProUser 
      ? 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)' 
      : hasPlan 
        ? 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)'
        : 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)',
    padding: '2px 6px',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: 'bold',
    position: 'absolute',
    top: isProUser ? '35px' : '0px',
    left: '45px',
    display: 'inline-flex',
    alignItems: 'center',
    height: '16px',
    width: isProUser ? '32px' : hasPlan ? '70px' : '95px',
    justifyContent: 'center',
    cursor: isProUser ? 'default' : 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    color: 'white'
  };

  const popoverStyle = {
    position: 'absolute',
    top: '25px',
    left: '45px',
    background: 'rgba(0, 0, 0, 0.9)',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    width: '200px',
    color: 'white',
    zIndex: 1000,
    pointerEvents: 'none',
    opacity: showPopover ? 1 : 0,
    transition: 'opacity 0.2s ease-in-out',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(5px)'
  };

  if (!userStore.user) return null;

  if (isProUser) {
    return (
      <div style={badgeStyle}>
        PRO
      </div>
    );
  }

  if (hasPlan) {
    return (
      <div style={{ position: 'relative' }}>
        <a 
          href={userStore.customerPortalUrl} 
          target="_blank" 
          style={badgeStyle}
          onMouseEnter={() => setShowPopover(true)}
          onMouseLeave={() => setShowPopover(false)}
          onClick={(e) => {
            if (!userStore.customerPortalUrl) {
              e.preventDefault();
            }
          }}
        >
          UPGRADE
        </a>
        <div style={popoverStyle}>
          Upgrade to pro to get premium voices, other languages, and more!
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <button 
        style={badgeStyle}
        onMouseEnter={() => setShowPopover(true)}
        onMouseLeave={() => setShowPopover(false)}
        onClick={(e) => {
          const hasStripeCustomerId = userStore.user?.stripe_customer_id || userStore.user?.customer_id;
          const hasActiveSubscription = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id;
          
          if (hasStripeCustomerId && !hasActiveSubscription) {
            createReactivationCheckoutSession(userStore.idToken, {}, '/add')
              .then(response => {
                if (response.data.url) {
                  window.location.href = response.data.url;
                } else {
                  window.location.href = '/onboarding/choosevoice';
                }
              })
              .catch(() => {
                window.location.href = '/onboarding/choosevoice';
              });
          } else {
            window.location.href = '/onboarding/choosevoice';
          }
        }}
      >
        UPGRADE
      </button>
      <div style={popoverStyle}>
        Start your 7-day free trial today!
      </div>
    </div>
  );
});

const SideBar = observer(
  ({ mode, changeMode, displayName, triggers, userFeedUrl, logOut }) => {
    const [lowCreditTrigger, setLowCreditTrigger] = useState();

    useEffect(() => {
      triggers.forEach((trigger) => {
        if (trigger.name === "low_on_credits") {
          setLowCreditTrigger(trigger);
        }
      });
    }, [triggers]);

    return (
      <div>
        <div className="branding">
          <div style={{ position: "relative" }}>
            <a href="">
              <img className="logo" src={logo} />
            </a>
            <ProBadge />
          </div>
        </div>
        <div className="nav-links">
          <a
            className={mode === "addArticle" ? "active" : "inactive"}
            onClick={() => changeMode("addArticle")}
          >
            <Plus />
            <span>Add Article</span>
          </a>
          <a
            className={mode === "playlist" ? "active" : "inactive"}
            onClick={() => changeMode("playlist")}
          >
            <Queue />
            <span>Playlist</span>
          </a>
          <a
            className={mode === "discover" ? "active" : "inactive"}
            onClick={() => changeMode("discover")}
            style={{ position: 'relative' }}
            onMouseEnter={(e) => {
              const tooltip = e.currentTarget.querySelector('.tooltip');
              if (tooltip) tooltip.style.opacity = 1;
            }}
            onMouseLeave={(e) => {
              const tooltip = e.currentTarget.querySelector('.tooltip');
              if (tooltip) tooltip.style.opacity = 0;
            }}
          >
            <RssFeedIcon style={{ height: "24px" }} />
            <span>Feeds</span>
            <div className="tooltip" style={{
              position: 'absolute',
              top: '50%',
              left: '100%',
              transform: 'translateY(-50%)',
              background: 'rgba(0, 0, 0, 0.9)',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              width: '250px',
              color: 'white',
              zIndex: 1000,
              pointerEvents: 'none',
              opacity: 0,
              transition: 'opacity 0.2s ease-in-out',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
              backdropFilter: 'blur(5px)',
              marginLeft: '10px'
            }}>
              Convert any blog, newsletter, or rss feed into a podcast instantly!
            </div>
          </a>
        </div>
        <div className="actions">
          {lowCreditTrigger && (
            <div
              style={{
                backgroundColor: "#d60000",
                padding: "5px 8px",
                borderRadius: "5px",
                textAlign: "center",
                width: "80%",
                margin: "auto",
              }}
            >
              <Link
                to={lowCreditTrigger.data.purchase_redirect}
                style={{
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "bold",
                  textDecoration: "none",
                }}
              >
                {lowCreditTrigger.data.main_header_text}
                <span
                  style={{ textDecoration: "underline", marginLeft: "5px" }}
                >
                  {lowCreditTrigger.data.button_text}
                </span>
              </Link>
            </div>
          )}

          {/* <a
            className="link"
            href="https://audiblogs.upvoty.com/"
            target="_blank"
          >
            <img src={Feature} />
            <span>Request a Feature</span>
          </a> */}
          {/* <a className="link" href="https://audioread.tolt.io/" target="_blank">
            <PaidIcon />
            <span>Earn 25% Commission</span>
          </a> */}

          {userFeedUrl && (
            <Link
              className="link"
              to={`/setupnew?feed_url=${encodeURIComponent(userFeedUrl)}`}
            >
              <RssFeedIcon />
              <span>Connect Podcast App</span>
            </Link>
          )}
          <Link className="link" to={`/settings`}>
            <AccountCircleIcon />
            <span>Settings</span>
          </Link>
          <a className="link" onClick={logOut}>
            <PowerSettingsNewIcon />
            <span>Log Out</span>
          </a>
          {/* <div className="profile">
            <img src={Profile} />
            <span>{displayName}</span>
          </div>
          <div style={{ textAlign: "center" }}>v{version}</div> */}
        </div>
      </div>
    );
  }
);

export default SideBar;
